import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Clock,
  LoaderCircle,
  Mail,
  MapPin,
  Phone,
  Send,
  ShieldCheck,
} from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { nome: "", azienda: "", email: "", telefono: "", servizio: "", messaggio: "" };

const inputCls =
  "w-full border border-[#272B36] bg-[#0D0E11] px-4 py-3 text-sm text-[#F8FAFC] placeholder-[#64748B] outline-none transition-colors focus:border-[#F97316]";
const labelCls =
  "mb-2 block font-mono-tech text-[11px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]";

export default function Contatti() {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setStatus({ type: null, message: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ type: "success", message: c.successMsg });
      toast.success(c.toastSuccess);
      setForm(EMPTY);
    } catch (err) {
      setStatus({ type: "error", message: c.errorMsg });
      toast.error(c.toastError);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="relative border-t border-[#272B36] bg-[#0F1115] py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          overline={c.overline}
          title={c.title}
          description={c.desc}
          testid="contact-section-title"
        />

        <div className="grid gap-12 lg:grid-cols-5 lg:gap-16">
          <Reveal className="lg:col-span-2">
            <div className="flex h-full flex-col gap-4">
              <div className="border border-[#272B36] bg-[#181B22] p-6">
                <Building2 className="h-6 w-6 text-[#F97316]" strokeWidth={1.5} />
                <p className="mt-4 font-mono-tech text-[11px] font-bold uppercase tracking-[0.2em] text-[#64748B]">
                  {c.labels.ragioneSociale}
                </p>
                <p className="mt-1 font-display text-2xl font-bold uppercase text-[#F8FAFC]">
                  Forte Servizii SRLS
                </p>
                <p data-testid="contact-info-vat" className="mt-2 font-mono-tech text-xs tracking-[0.15em] text-[#94A3B8]">
                  P.IVA 05072040230
                </p>
              </div>
              <div className="border border-[#272B36] bg-[#181B22] p-6">
                <MapPin className="h-6 w-6 text-[#F97316]" strokeWidth={1.5} />
                <p className="mt-4 font-mono-tech text-[11px] font-bold uppercase tracking-[0.2em] text-[#64748B]">
                  {c.labels.sedeLegale}
                </p>
                <p data-testid="contact-info-address" className="mt-1 text-base font-semibold text-[#F8FAFC]">
                  Via Fabio Filzi 6/A
                </p>
                <p className="text-sm text-[#94A3B8]">37047 San Bonifacio (VR)</p>
              </div>
              <a
                data-testid="contact-info-email-link"
                href="mailto:Forteservizii2000@gmail.com"
                className="group border border-[#272B36] bg-[#181B22] p-6 transition-colors hover:border-[#F97316]/60"
              >
                <Mail className="h-6 w-6 text-[#F97316]" strokeWidth={1.5} />
                <p className="mt-4 font-mono-tech text-[11px] font-bold uppercase tracking-[0.2em] text-[#64748B]">
                  {c.labels.email}
                </p>
                <p className="mt-1 break-all text-base font-semibold text-[#F8FAFC] transition-colors group-hover:text-[#F97316]">
                  Forteservizii2000@gmail.com
                </p>
              </a>
              <a
                data-testid="contact-info-phone-link"
                href="tel:+393931961669"
                className="group border border-[#272B36] bg-[#181B22] p-6 transition-colors hover:border-[#F97316]/60"
              >
                <Phone className="h-6 w-6 text-[#F97316]" strokeWidth={1.5} />
                <p className="mt-4 font-mono-tech text-[11px] font-bold uppercase tracking-[0.2em] text-[#64748B]">
                  {c.labels.telefono}
                </p>
                <p className="mt-1 text-base font-semibold text-[#F8FAFC] transition-colors group-hover:text-[#F97316]">
                  +39 393 196 1669
                </p>
              </a>
              <div className="border border-[#272B36] bg-[#181B22] p-6">
                <ShieldCheck className="h-6 w-6 text-[#F97316]" strokeWidth={1.5} />
                <p className="mt-4 font-mono-tech text-[11px] font-bold uppercase tracking-[0.2em] text-[#64748B]">
                  {c.certTitle}
                </p>
                <ul data-testid="contact-info-certifications" className="mt-3 space-y-2">
                  {c.certifications.map((cert) => (
                    <li key={cert} className="flex items-center gap-2 text-sm text-[#94A3B8]">
                      <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-[#E65100]" aria-hidden="true" />
                      {cert}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-auto flex items-center gap-3 border border-[#E65100]/40 bg-[#E65100]/10 px-5 py-4">
                <Clock className="h-5 w-5 shrink-0 text-[#F97316]" strokeWidth={1.5} />
                <p className="text-sm text-[#F8FAFC]">
                  {c.responsePre} <strong>{c.responseStrong}</strong>
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-3">
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="border border-[#272B36] bg-[#181B22] p-7 sm:p-10"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-nome" className={labelCls}>{c.labels.nome}</label>
                  <input
                    id="cf-nome"
                    data-testid="contact-form-name"
                    required
                    minLength={2}
                    value={form.nome}
                    onChange={set("nome")}
                    placeholder={c.placeholders.nome}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="cf-azienda" className={labelCls}>{c.labels.azienda}</label>
                  <input
                    id="cf-azienda"
                    data-testid="contact-form-company"
                    required
                    minLength={2}
                    value={form.azienda}
                    onChange={set("azienda")}
                    placeholder={c.placeholders.azienda}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className={labelCls}>{c.labels.email} *</label>
                  <input
                    id="cf-email"
                    data-testid="contact-form-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder={c.placeholders.email}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="cf-telefono" className={labelCls}>{c.labels.telefono}</label>
                  <input
                    id="cf-telefono"
                    data-testid="contact-form-phone"
                    type="tel"
                    value={form.telefono}
                    onChange={set("telefono")}
                    placeholder={c.placeholders.telefono}
                    className={inputCls}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-servizio" className={labelCls}>{c.labels.servizio}</label>
                  <select
                    id="cf-servizio"
                    data-testid="contact-form-service"
                    required
                    value={form.servizio}
                    onChange={set("servizio")}
                    className={`${inputCls} appearance-none ${form.servizio ? "" : "text-[#64748B]"}`}
                  >
                    <option value="" disabled>{c.selectPlaceholder}</option>
                    {c.serviceOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-messaggio" className={labelCls}>{c.labels.messaggio}</label>
                  <textarea
                    id="cf-messaggio"
                    data-testid="contact-form-message"
                    required
                    minLength={10}
                    rows={5}
                    value={form.messaggio}
                    onChange={set("messaggio")}
                    placeholder={c.placeholders.messaggio}
                    className={`${inputCls} resize-y`}
                  />
                </div>
              </div>

              {status.type && (
                <div
                  data-testid="contact-form-status-alert"
                  role="alert"
                  className={`mt-6 flex items-start gap-3 border px-4 py-3 text-sm ${
                    status.type === "success"
                      ? "border-emerald-600/50 bg-emerald-600/10 text-emerald-300"
                      : "border-red-600/50 bg-red-600/10 text-red-300"
                  }`}
                >
                  {status.type === "success" ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  ) : (
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  )}
                  {status.message}
                </div>
              )}

              <button
                data-testid="contact-form-submit-button"
                type="submit"
                disabled={sending}
                className="group mt-8 flex w-full items-center justify-center gap-3 bg-[#E65100] px-8 py-4 font-mono-tech text-sm font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#F97316] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? (
                  <>
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    {c.sending}
                  </>
                ) : (
                  <>
                    {c.submit}
                    <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
